import { displayUpdateInfo } from "@modules/bookmarks/advert-page/bookmarks.ts"
import { deleteCookieEntry, deleteEntry } from "@modules/bookmarks/entries/entries-api"
import { type EntryOptions, getOptionButtonsForCard } from "@modules/bookmarks/entries/entry-options"
import { renderEntryRatings } from "@modules/bookmarks/entries/entry-ratings"
import { TagApplyEvent } from "@modules/bookmarks/tags/Tag.ts"
import {
    TagDeleteSuccessEvent,
    TagRenameSuccessEvent,
    type TagsListData,
    tagsList,
} from "@modules/bookmarks/tags/tag-list"
import { openTagDropdownForCard } from "@modules/bookmarks/tags/tags-dropdown"
import { makeTagsListItem } from "@modules/bookmarks/tags/tags-list-item"
import type { JQuery } from "@modules/jquery-types"
import { addEventListenerAll } from "@modules/util/events"
import { AdvertShareModal, type AdvertShareModalData } from "./AdvertShareModal"
import { NotesRatingModal, type NotesRatingModalData } from "./NotesRatingModal"

const entryCardsSelector: string = "#entry-list .entry-card"
const userId: number | null = userData?.userId ?? 0

export type HTMLTagsListElement = HTMLArticleElement & {
    tagsList: TagsListData[]
}

const sortByReferenceTagsList = (unsortedList: TagsListData[]): TagsListData[] => {
    const getTagPosition = (tagSlug: string): number => {
        const position = tagsList.findIndex((referenceTag) => referenceTag.tagSlug === tagSlug)
        return position === -1 ? Number.POSITIVE_INFINITY : position
    }

    return unsortedList.sort((tagA, tagB) => getTagPosition(tagA.tagSlug) - getTagPosition(tagB.tagSlug))
}

const updateCardTags = (entryCard: HTMLTagsListElement, tagsListElement: HTMLUListElement): void => {
    requestAnimationFrame((): void => {
        tagsListElement.textContent = ""

        for (const tag of entryCard.tagsList) {
            const cardTag = makeTagsListItem(tag.tagName, tag.tagSlug)
            tagsListElement.appendChild(cardTag)
        }
    })
}

export const initializeCards = (): void => {
    const cards: NodeListOf<HTMLTagsListElement> = document.querySelectorAll<HTMLTagsListElement>(entryCardsSelector)

    for (const entryCard of cards) {
        const cardOptions: EntryOptions = getOptionButtonsForCard(entryCard)
        const entryCanonicalId: string = entryCard.dataset?.canonicalId ?? ""
        const entryAdvertId: number = Number.parseInt(entryCard.dataset?.advertId ?? "0", 10)

        addEventListenerAll(cardOptions.deleteButton(), "click", async (): Promise<void> => {
            const rating = Number.parseInt(
                (entryCard.querySelector(".entry-rating-stars") as HTMLElement)?.dataset.userRating || "0",
                10,
            )
            const note = entryCard.querySelector(".entry-notes-text")?.textContent?.trim() || ""
            const hasRatingOrNote = rating > 0 || note.length > 0

            if (hasRatingOrNote) {
                const confirmReviewedEntryDeletionModal = document.getElementById(
                    "confirm-delete-reviewed-entry-modal",
                ) as HTMLDivElement | null
                const confirmButton = confirmReviewedEntryDeletionModal?.querySelector(
                    "#confirm-delete-reviewed-entry-button",
                ) as HTMLButtonElement | null
                if (confirmReviewedEntryDeletionModal && confirmButton) {
                    $(confirmReviewedEntryDeletionModal).modal("show")
                    confirmButton.onclick = async (): Promise<void> => {
                        const success =
                            userId === 0
                                ? await deleteCookieEntry(entryCanonicalId, entryAdvertId)
                                : await deleteEntry(userId, entryCanonicalId, entryAdvertId)

                        if (success) {
                            displayUpdateInfo("Die Anzeige wurde erfolgreich aus dem Merkzettel entfernt.")
                        } else {
                            displayUpdateInfo(
                                "Beim Entfernen der Anzeige aus dem Merkzettel ist ein Fehler aufgetreten. Bitte versuche es erneut.",
                            )
                        }

                        $(confirmReviewedEntryDeletionModal).modal("hide")
                    }
                }
            } else {
                const success =
                    userId === 0
                        ? await deleteCookieEntry(entryCanonicalId, entryAdvertId)
                        : await deleteEntry(userId, entryCanonicalId, entryAdvertId)

                if (success) {
                    displayUpdateInfo("Die Anzeige wurde erfolgreich aus dem Merkzettel entfernt.")
                } else {
                    displayUpdateInfo(
                        "Beim Entfernen der Anzeige aus dem Merkzettel ist ein Fehler aufgetreten. Bitte versuche es erneut.",
                    )
                }
            }
        })

        addEventListenerAll(cardOptions.shareButton(), "click", (): void => {
            AdvertShareModal.updateModalData({
                cityName:
                    entryCard.querySelector(".entry-card--contact-box-contents div:nth-child(1) > h2 > strong")
                        ?.textContent ?? "",
                socialMediaLink: `${SiteInfo.baseLink}${entryCard.dataset.advertId}`,
                thumbnailSrc: entryCard.dataset.thumbnailUrl,
                title: entryCard.dataset.advertName,
                clubSlogan: entryCard.querySelector('.advert-text span[property="clubSlogan"]')?.textContent,
            } as AdvertShareModalData)
            AdvertShareModal.modal<JQuery>($).modal("show")
        })

        document.addEventListener(TagDeleteSuccessEvent, (tagEvent): void => {
            if (entryCard.tagsList.length === 0) {
                return
            }

            entryCard.tagsList = entryCard.tagsList.filter(
                (tag: TagsListData): boolean => tag.tagSlug !== tagEvent.detail.tagSlug,
            )
        })

        document.addEventListener(TagRenameSuccessEvent, (tagEvent): void => {
            if (entryCard.tagsList.length === 0) {
                return
            }

            const cardTags: TagsListData[] = entryCard.tagsList
            const changedTag: TagsListData | undefined = cardTags.find(
                (tag: TagsListData): boolean => tag.tagSlug === tagEvent.detail.oldTag.tagSlug,
            )

            if (changedTag) {
                changedTag.tagSlug = tagEvent.detail.newTag.tagSlug
                changedTag.tagName = tagEvent.detail.newTag.tagName

                entryCard.tagsList = cardTags
            }
        })

        document.addEventListener(TagApplyEvent, (tagEvent): void => {
            const tag: TagsListData = {
                tagSlug: tagEvent.detail.tag.tagSlug,
                tagName: tagEvent.detail.tag.tagName,
            }

            const isSelected = tagEvent.detail.tag.tagElement.classList.contains("tag-selected")

            if (tagEvent.detail.tag.tagSlug === "-" && tagEvent.detail.tag.tagName === "Ohne Tag") {
                const hasNoTags = entryCard.tagsList.length === 0

                if (!hasNoTags && isSelected) {
                    entryCard.classList.add("hidden")
                } else {
                    entryCard.classList.remove("hidden")
                }

                return
            }

            const exists = entryCard.tagsList.some((entryTag: TagsListData) => entryTag.tagSlug === tag.tagSlug)

            if (!exists && isSelected) {
                entryCard.classList.add("hidden")
            } else {
                entryCard.classList.remove("hidden")
            }
        })

        if (userId === 0) {
            const tagButtons = cardOptions.tagButton()
            for (let buttonIndex = 0; buttonIndex < tagButtons.length; buttonIndex++) {
                const button = tagButtons[buttonIndex]
                button.remove()
            }

            const notesButtons = cardOptions.notesRatingButton()
            for (let buttonIndex = 0; buttonIndex < notesButtons.length; buttonIndex++) {
                const button = notesButtons[buttonIndex]
                button.remove()
            }
            continue
        }

        const tagsListElement = entryCard.querySelector<HTMLUListElement>(".entry-tags-list")

        Object.defineProperty(entryCard, "tagsList", {
            get: (): TagsListData[] => {
                if (!tagsListElement) {
                    return []
                }

                const userTags = tagsListElement.dataset.userTags ?? ""

                if (userTags === "") {
                    return []
                }

                return JSON.parse(userTags)
            },
            set: (list: TagsListData[]): void => {
                if (!tagsListElement) {
                    return
                }

                tagsListElement.dataset.userTags = JSON.stringify(sortByReferenceTagsList(list))
                updateCardTags(entryCard, tagsListElement)
            },
        })

        if (tagsListElement) {
            updateCardTags(entryCard, tagsListElement)
        }

        addEventListenerAll(
            cardOptions.tagButton(),
            "click",
            (clickEvent: PointerEvent): void => {
                clickEvent.stopPropagation()
                openTagDropdownForCard(entryCard)
            },
            { passive: true },
        )

        addEventListenerAll(cardOptions.notesRatingButton(), "click", (): void => {
            const ratingElement = entryCard.querySelector(".entry-rating-stars") as HTMLUListElement | null
            const notesElement = entryCard.querySelector(".entry-notes-text") as HTMLElement | null

            let rating = ratingElement?.dataset.userRating
            if (!rating) {
                rating = "0"
            }

            let notes = notesElement?.textContent
            if (!notes) {
                notes = ""
            }

            NotesRatingModal.updateModalData({
                userId,
                canonicalId: entryCanonicalId,
                advertId: entryAdvertId,
                rating: Number.parseFloat(rating),
                notes,
                successCallback: (rating: number, notes: string): void => {
                    if (ratingElement) {
                        ratingElement.dataset.userRating = rating.toString()
                    }
                    if (notesElement) {
                        notesElement.textContent = notes
                    }

                    renderEntryRatings(entryCard)
                },
            } as NotesRatingModalData)
            NotesRatingModal.modal<JQuery>($).modal("show")
        })
    }
}
